import type { AudioToPresetSearchResultData } from "@/client/core/services/audio-to-preset-search.service";
import type { SpliceSearchResult } from "@/shared/splice-search.api";

/**
 * Preset search answers with ids, preview links and the index each match
 * came from, and nothing else: no name, no device. The dock lists presets
 * in the same rows as samples, so each match becomes a result with what is
 * known and `--` for the rest. The preview is real audio, so a row plays,
 * draws a waveform and drags out like any sample.
 */

/** What the dock calls a preset row's category, where a sample says `loop` or `oneshot`. */
export const kPresetAssetCategory = "preset";

const kIndexLabels: Readonly<Record<string, string>> = {
    all_presets: "All presets",
    premium_presets: "Premium",
    instrument_presets: "Instrument",
};

/**
 * The best name on offer: the preview file's own name when it carries one,
 * else the preset's short id. A preview link that ends in the id, or in
 * something that is not a name, falls through to the id.
 */
export const describePreset = (uuid: string, previewUrl: string): string => {
    const path = previewUrl.split(/[?#]/)[0] ?? "";
    const file = decodeURIComponent(path.split("/").pop() ?? "").replace(/\.\w+$/, "");
    const looksLikeName = file.length > 0 && !file.includes(uuid) && /[a-zA-Z]{3,}/.test(file);
    return looksLikeName ? file : `Preset ${uuid.slice(0, 8)}`;
};

/**
 * Rows for the matches. The catalog's name and plugin lead when the server
 * could fetch them; otherwise the preview file's name and the index the
 * match came from stand in. The plugin travels as the row's one tag, and as
 * the provider, so a drag-out carries it too.
 */
export const toPresetResults = (
    data: AudioToPresetSearchResultData,
): readonly SpliceSearchResult[] => {
    const itemsByUuid = new Map((data.items ?? []).map((item) => [item.uuid, item]));
    return data.uuids.map((uuid, index) => {
        const previewUrl = data.urls[index] ?? "";
        const sourceIndex = data.sourceIndexes[index] ?? "";
        const item = itemsByUuid.get(uuid);
        const device = item?.device ?? null;
        const indexLabel =
            sourceIndex === "" ? undefined : (kIndexLabels[sourceIndex] ?? sourceIndex);
        return {
            previewUrl,
            metadata: {
                uuid,
                name: item?.name ?? describePreset(uuid, previewUrl),
                bpm: 0,
                key: "",
                chordType: "",
                duration: 0,
                assetCategory: kPresetAssetCategory,
                tags: device !== null ? [device] : indexLabel !== undefined ? [indexLabel] : [],
                provider: device ?? "",
                packName: "",
                packArtworkUrl: "",
            },
        };
    });
};
